import React, { useState, useMemo } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Tabs, Tab } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import OverviewInfoTable from './OverviewInfoTable';
import SpeciesTable from './SpeciesTable';
import ProductsTable from './ProductsTable';
import SampleDataViewCard from '../SampleDataViewCard';
import ViewSamples from '../ViewSamples';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const SubCompartmentSection = ({
  createdAssessmentId,
  subCompartments = [],
  pins = [],
  samplePoints = [],
  samplesData = [],
  dropDownOptions = {},
  handleAction,
  handleSampleData,
  handleOverviewUpdate,
  selectedCategory,
}) => {
  const [expandedSubComp, setExpandedSubComp] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Check if category is Haulage or Chipping
  const isProductsOnly = selectedCategory === 'Haulage' || selectedCategory === 'Chipping';

  // Group samples by sub-compartment
  const subCompartmentsWithSamples = useMemo(() => {
    return subCompartments.map(subComp => {
      const subCompName = subComp.mapLabel;

      const relatedSamples = pins
        .filter(pin => {
          return pin.mapLabel?.toLowerCase().startsWith('sample point') && pin.relatedTo === subCompName;
        })
        .map(pin => {
          const sampleName = pin.mapLabel;
          const points = samplePoints.filter(p => p.sampleName === sampleName);
          const sampleDataEntry = Array.isArray(samplesData) ? samplesData.find(d => d.sampleName === sampleName) : null;

          return {
            pinId: pin.id,
            sampleName,
            mapColor: pin.hexColor || 'gray',
            samplePoints: points,
            sampleData: sampleDataEntry,
          };
        });

      return {
        ...subComp,
        samples: relatedSamples,
      };
    });
  }, [subCompartments, pins, samplePoints, samplesData]);

  const handleAccordionToggle = subCompId => {
    setExpandedSubComp(prev => (prev === subCompId ? null : subCompId));
    setTabValue(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!subCompartments || subCompartments.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Sub Compartments
      </Typography>

      {subCompartmentsWithSamples.map(subComp => (
        <Accordion
          key={subComp.id}
          expanded={expandedSubComp === subComp.id}
          onChange={() => handleAccordionToggle(subComp.id)}
          sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: subComp.hexColor || 'purple',
                  borderRadius: 1,
                }}
              />
              <Typography fontWeight={500}>{subComp.mapLabel}</Typography>
              <Typography variant="caption" color="text.secondary">
                ({subComp.area || 0} Ha)
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ width: '100%' }}>
              {isProductsOnly ? (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Products
                  </Typography>
                  <ProductsTable
                    subCompartmentId={subComp.id}
                    subCompartmentData={subComp}
                    createdAssessmentId={createdAssessmentId}
                  />
                </>
              ) : (
                <>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth">
                    <Tab label="Overview Info" />
                    <Tab label="Samples" />
                    <Tab label="Species" />
                    <Tab label="Products" />
                  </Tabs>

                  {/* Tab 0: Overview Info */}
                  <TabPanel value={tabValue} index={0}>
                    <OverviewInfoTable
                      subCompartmentId={subComp.id}
                      subCompartmentData={subComp}
                      onUpdate={handleOverviewUpdate}
                    />
                  </TabPanel>

                  {/* Tab 1: Samples */}
                  <TabPanel value={tabValue} index={1}>
                    {subComp.samples && subComp.samples.length > 0 ? (
                      <Box display="flex" flexDirection="column" gap={3}>
                        {subComp.samples.map(sample => (
                          <Box key={sample.pinId}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                              {sample.sampleName}
                            </Typography>

                            <SampleDataViewCard
                              samplePoints={sample.samplePoints}
                              samplesData={{
                                ...sample.sampleData,
                                sampleName: sample.sampleName,
                              }}
                              handleSampleData={handleSampleData}
                            />

                            <ViewSamples
                              samples={sample.samplePoints}
                              sampleName={sample.sampleName}
                              dropDownOptions={{
                                species: dropDownOptions,
                              }}
                              handleAction={handleAction}
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">No samples found for this sub-compartment</Typography>
                    )}
                  </TabPanel>

                  {/* Tab 2: Species */}
                  <TabPanel value={tabValue} index={2}>
                    <SpeciesTable
                      subCompartmentId={subComp.id}
                      samples={subComp.samples.flatMap(s => s.samplePoints || [])}
                      areaInHa={subComp.area}
                    />
                  </TabPanel>

                  {/* Tab 3: Products */}
                  <TabPanel value={tabValue} index={3}>
                    <ProductsTable
                      subCompartmentId={subComp.id}
                      subCompartmentData={subComp}
                      createdAssessmentId={createdAssessmentId}
                    />
                  </TabPanel>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default SubCompartmentSection;
